import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

const HAIR_OPTIONS = [
  "Excessive shedding", "Diffuse thinning", "Crown thinning", "Frontal recession",
  "Patchy hair loss", "Breakage", "Dry scalp", "Oily scalp", "Itching", "Burning",
  "Tenderness", "Flaking/scaling", "Redness/inflammation", "Bumps or sores",
  "Traction/tension history", "Protective style concerns", "Chemical/color damage",
  "Slow growth", "Texture changes", "No current hair/scalp concern",
];
const WELLNESS_OPTIONS = [
  "Fatigue", "Brain fog", "Poor focus", "Anxiety", "Low mood", "Grief/emotional stress",
  "High stress", "Poor sleep", "Insomnia", "Headaches", "Bloating", "Constipation",
  "Diarrhea", "Acid reflux", "Sugar cravings", "Weight gain", "Weight loss",
  "Hot flashes", "Night sweats", "Irregular cycle", "Heavy cycle", "PMS symptoms",
  "Low libido", "Skin changes/acne", "Inflammation/joint pain", "Frequent urination",
  "Recurrent UTIs", "Low water intake", "No current wellness concern",
];

// ── 2026-02-28 · Additive KCIS clinical intake field option sets ──
// All option lists are appended below the existing chip groups in the UI so
// historical assessments and downstream Airtable/Make consumers continue to
// see the original fields unchanged.
const HEIGHT_OPTIONS = ["Under 5'", "5'0\"–5'4\"", "5'5\"–5'8\"", "5'9\"–6'0\"", "Over 6'"];
const BLOOD_TYPE_OPTIONS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"];
const RACE_ETHNICITY_OPTIONS = ["African American / Black", "White / Caucasian", "Hispanic / Latino", "Asian", "Native American", "Pacific Islander", "Multiracial", "Other", "Prefer Not To Say"];
const MENOPAUSAL_OPTIONS = ["Pre-Menopausal", "Perimenopausal", "Post-Menopausal", "Unsure"];
const PREGNANCY_OPTIONS = ["Pregnant", "Trying To Conceive", "Not Pregnant", "N/A"];

const HAIR_SYMPTOMS = ["Hair not growing", "Hair growing slowly", "Hair feels thinner", "Hair texture changed", "Hair breaks easily"];
const SCALP_SYMPTOMS = ["Scalp odor", "Scalp pain", "Folliculitis", "Excess sweating"];
const DIGESTIVE_SYMPTOMS = ["Bloating", "Gas", "Constipation", "Diarrhea", "Acid Reflux", "Food Sensitivities", "Nausea", "Gallbladder Issues"];
const HORMONAL_SYMPTOMS = ["Hot Flashes", "Night Sweats", "Low Libido", "Irregular Cycle", "Heavy Cycle", "PMS", "Infertility", "Facial Hair Growth"];
const EMOTIONAL_SYMPTOMS = ["Brain Fog", "Poor Focus", "Forgetfulness", "Anxiety", "Depression", "Overwhelm", "Mood Swings", "Grief"];
const NUTRIENT_INDICATORS = ["Cold Hands/Feet", "White Nail Spots", "Brittle Nails", "Muscle Cramps", "Restless Legs", "Dizziness", "Frequent Illness", "Poor Wound Healing"];
const DIAGNOSED_CONDITIONS = [
  "Diabetes", "Prediabetes", "Hypertension", "Hypothyroidism", "Hyperthyroidism",
  "Hashimoto's", "PCOS", "Fibroids", "Endometriosis", "Anxiety", "Depression",
  "ADHD", "Autoimmune Disease", "IBS", "Crohn's", "Ulcerative Colitis",
  "High Cholesterol", "Sleep Apnea", "Other",
];

const EXERCISE_OPTIONS = ["None", "1–2 Days Weekly", "3–4 Days Weekly", "5+ Days Weekly"];
const BOWEL_OPTIONS = ["Daily", "Every Other Day", "2–3 Times Weekly", "Less Than 3 Weekly"];
const DIETARY_OPTIONS = ["Standard American Diet", "Vegetarian", "Vegan", "Keto", "Low Carb", "Mediterranean", "Mixed"];
const ALCOHOL_OPTIONS = ["Never", "Rarely", "Weekly", "Multiple Times Weekly", "Daily"];
const TOBACCO_OPTIONS = ["Current", "Former", "Never"];
const PRIMARY_STRESSOR_OPTIONS = ["Financial", "Relationship", "Work", "Health", "Grief", "Caregiving", "Other"];
const FUNCTIONAL_TESTING_OPTIONS = ["None", "Bloodwork", "HTMA", "GI Testing", "Hormone Testing", "Food Sensitivity Testing", "Other"];
const LAB_PANEL_OPTIONS = [
  "CBC", "CMP", "Lipid Panel", "Thyroid Panel", "Iron Panel", "Vitamin D",
  "Hormone Panel", "HTMA", "GI Map", "Food Sensitivity", "Other",
];

const PRIMARY_WELLNESS_GOAL_OPTIONS = ["Weight Loss", "Hair Growth", "Hormonal Balance", "Digestive Health", "Stress Reduction", "Energy", "Mental Clarity", "Overall Wellness"];
const PREFERRED_APPROACH_OPTIONS = ["Herbal", "Lifestyle", "Nutrition", "Functional Testing", "Combination Approach"];

const Section = ({ title, subtitle, children }) => (
  <div className="kcis-fade-up">
    <div className="kcis-overline text-purple-900">{subtitle}</div>
    <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-slate-900 mt-2">{title}</h2>
    <div className="mt-8 space-y-6">{children}</div>
  </div>
);

const inputCls = "rounded-xl border-slate-200 bg-slate-50/60 focus:bg-white h-12 px-4";

const ChipGroup = ({ options, value, onChange, testidPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const active = value.includes(opt);
      return (
        <button
          type="button"
          key={opt}
          onClick={() => onChange(active ? value.filter((v) => v !== opt) : [...value, opt])}
          className={`rounded-full px-4 py-2 text-sm border transition-all ${active ? "bg-purple-900 border-purple-900 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-purple-300"}`}
          data-testid={`${testidPrefix}-${opt.replace(/\W+/g, "-").toLowerCase()}`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

export default function Intake() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const practitionerId = params.get("practitioner") || "";
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: params.get("prefill_name") || "",
    email: params.get("prefill_email") || "",
    phone: params.get("prefill_phone") || "",
    age: "",
    biological_sex: "",
    gender_identity: "",
    gender_identity_text: "",
    primary_concern: params.get("prefill_concern") || "",
    symptoms: [],
    hair_scalp_concerns: [],
    wellness_concerns: [],
    stress_level: 5,
    sleep_hours: 7,
    water_intake_oz: 64,
    medications: "",
    allergies: "",
    goals: "",
    // ── KCIS clinical intake additions (2026-02-28) ──
    height_range: "",
    weight: "",
    blood_type: "",
    race_ethnicity: "",
    menopausal_status: "",
    pregnancy_status: "",
    hair_symptoms: [],
    scalp_symptoms: [],
    digestive_symptoms: [],
    hormonal_symptoms: [],
    emotional_symptoms: [],
    nutrient_indicators: [],
    diagnosed_conditions: [],
    exercise_frequency: "",
    bowel_frequency: "",
    dietary_pattern: "",
    alcohol_use: "",
    tobacco_use: "",
    primary_stressor: "",
    functional_testing_history: [],
    lab_results_available: "",
    lab_panels_available: [],
    primary_wellness_goal: "",
    preferred_approach: "",
    readiness_level: 5,
  });

  // If query string changes (e.g. practitioner deeplink re-opened), don't clobber typed input.
  useEffect(() => { /* params snapshot taken on mount */ }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 1) {
      if (!form.name || !form.email || !form.phone || !form.age) {
        toast.error("Please complete all contact fields.");
        return false;
      }
      if (!form.biological_sex) {
        toast.error("Please select your biological sex.");
        return false;
      }
    }
    if (step === 2 && !form.primary_concern.trim()) {
      toast.error("Please share your primary concern.");
      return false;
    }
    if (step === 4 && !form.goals.trim()) {
      toast.error("Please share your goals.");
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(totalSteps, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        age: Number(form.age),
        sleep_hours: Number(form.sleep_hours),
        water_intake_oz: Number(form.water_intake_oz),
        gender_identity: form.gender_identity || null,
        gender_identity_text: form.gender_identity === "self_describe" ? form.gender_identity_text : "",
        // Cast additive optional numerics; preserve blanks so Pydantic treats them as None.
        weight: form.weight === "" ? null : Number(form.weight),
        readiness_level: form.readiness_level === "" ? null : Number(form.readiness_level),
      };
      const { data } = await api.post(
        practitionerId ? `/clients?practitioner_id=${encodeURIComponent(practitionerId)}` : "/clients",
        payload,
      );
      const assessmentId = data.assessment_id;
      if (data.access_token) sessionStorage.setItem("kcis_assessment_token", data.access_token);
      toast.success("Intake received — generating your protocol report...");
      try {
        await api.post(`/assessments/${assessmentId}/report`);
      } catch (e) {
        console.error(e);
        toast.error("Report generation had an issue. You can retry from your dashboard.");
      }
      navigate(`/report/${assessmentId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        <div className="flex items-center justify-between mb-3">
          <div className="kcis-overline">Step {step} of {totalSteps}</div>
          <div className="text-xs text-slate-500">{Math.round((step / totalSteps) * 100)}% complete</div>
        </div>
        <Progress value={(step / totalSteps) * 100} className="h-1.5 bg-slate-200" />

        <div className="mt-10 kcis-card p-7 sm:p-10">
          {step === 1 && (
            <Section title="Begin Your Wellness Assessment" subtitle="01 · Demographics">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-sm text-slate-700">Full name</Label>
                  <Input data-testid="intake-name" className={inputCls + " mt-2"} value={form.name} onChange={(e) => set("name", e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Email</Label>
                  <Input data-testid="intake-email" type="email" className={inputCls + " mt-2"} value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Phone</Label>
                  <Input data-testid="intake-phone" className={inputCls + " mt-2"} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Age</Label>
                  <Input data-testid="intake-age" type="number" min="1" className={inputCls + " mt-2"} value={form.age} onChange={(e) => set("age", e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Height range (optional)</Label>
                  <Select value={form.height_range} onValueChange={(v) => set("height_range", v)}>
                    <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-height">
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent>
                      {HEIGHT_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Weight (lbs, optional)</Label>
                  <Input data-testid="intake-weight" type="number" min="0" step="0.1" className={inputCls + " mt-2"} value={form.weight} onChange={(e) => set("weight", e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Blood type (optional)</Label>
                  <Select value={form.blood_type} onValueChange={(v) => set("blood_type", v)}>
                    <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-blood-type">
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent>
                      {BLOOD_TYPE_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Race / ethnicity (optional)</Label>
                  <Select value={form.race_ethnicity} onValueChange={(v) => set("race_ethnicity", v)}>
                    <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-race-ethnicity">
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent>
                      {RACE_ETHNICITY_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Some patterns differ across populations — this helps refine clinical reasoning.</p>
                </div>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Biological sex <span className="text-purple-900">*</span></Label>
                <RadioGroup
                  className="mt-3 grid sm:grid-cols-2 gap-2"
                  value={form.biological_sex}
                  onValueChange={(v) => set("biological_sex", v)}
                >
                  {[
                    { v: "female", l: "Female" },
                    { v: "male", l: "Male" },
                    { v: "intersex", l: "Intersex" },
                    { v: "prefer_not_to_say", l: "Prefer not to say" },
                  ].map((opt) => (
                    <Label
                      key={opt.v}
                      htmlFor={`bio-${opt.v}`}
                      className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-all ${form.biological_sex === opt.v ? "border-purple-900 bg-purple-50" : "border-slate-200 bg-white hover:border-purple-300"}`}
                    >
                      <RadioGroupItem value={opt.v} id={`bio-${opt.v}`} data-testid={`intake-bio-${opt.v}`} />
                      <span className="text-sm text-slate-800">{opt.l}</span>
                    </Label>
                  ))}
                </RadioGroup>
                <p className="text-xs text-slate-500 mt-2">Used to personalize hormonal and physiological wellness considerations.</p>
              </div>
              {form.biological_sex === "female" && (
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm text-slate-700">Menopausal status (optional)</Label>
                    <Select value={form.menopausal_status} onValueChange={(v) => set("menopausal_status", v)}>
                      <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-menopausal">
                        <SelectValue placeholder="Choose…" />
                      </SelectTrigger>
                      <SelectContent>
                        {MENOPAUSAL_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-700">Pregnancy status (optional)</Label>
                    <Select value={form.pregnancy_status} onValueChange={(v) => set("pregnancy_status", v)}>
                      <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-pregnancy">
                        <SelectValue placeholder="Choose…" />
                      </SelectTrigger>
                      <SelectContent>
                        {PREGNANCY_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-sm text-slate-700">Gender identity (optional)</Label>
                <Select value={form.gender_identity} onValueChange={(v) => set("gender_identity", v)}>
                  <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-gender">
                    <SelectValue placeholder="Choose if you'd like to share..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="woman">Woman</SelectItem>
                    <SelectItem value="man">Man</SelectItem>
                    <SelectItem value="non_binary">Non-binary</SelectItem>
                    <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                    <SelectItem value="self_describe">Self-describe</SelectItem>
                  </SelectContent>
                </Select>
                {form.gender_identity === "self_describe" && (
                  <Input
                    data-testid="intake-gender-text"
                    className={inputCls + " mt-3"}
                    placeholder="How would you like to describe your gender?"
                    value={form.gender_identity_text}
                    onChange={(e) => set("gender_identity_text", e.target.value)}
                  />
                )}
              </div>
            </Section>
          )}

          {step === 2 && (
            <Section title="Your concerns" subtitle="02 · Health & Hair">
              <div>
                <Label className="text-sm text-slate-700">Primary concern</Label>
                <Textarea data-testid="intake-primary-concern" className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 min-h-[100px]" value={form.primary_concern} onChange={(e) => set("primary_concern", e.target.value)} placeholder="What brought you in today?" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Hair & scalp concerns</Label>
                <div className="mt-3"><ChipGroup options={HAIR_OPTIONS} value={form.hair_scalp_concerns} onChange={(v) => set("hair_scalp_concerns", v)} testidPrefix="hair" /></div>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Wellness concerns / symptoms</Label>
                <div className="mt-3"><ChipGroup options={WELLNESS_OPTIONS} value={form.wellness_concerns} onChange={(v) => set("wellness_concerns", v)} testidPrefix="wellness" /></div>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Additional hair symptoms</Label>
                <div className="mt-3"><ChipGroup options={HAIR_SYMPTOMS} value={form.hair_symptoms} onChange={(v) => set("hair_symptoms", v)} testidPrefix="hair-sym" /></div>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Additional scalp symptoms</Label>
                <div className="mt-3"><ChipGroup options={SCALP_SYMPTOMS} value={form.scalp_symptoms} onChange={(v) => set("scalp_symptoms", v)} testidPrefix="scalp-sym" /></div>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Digestive symptoms</Label>
                <div className="mt-3"><ChipGroup options={DIGESTIVE_SYMPTOMS} value={form.digestive_symptoms} onChange={(v) => set("digestive_symptoms", v)} testidPrefix="dig-sym" /></div>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Hormonal symptoms</Label>
                <div className="mt-3"><ChipGroup options={HORMONAL_SYMPTOMS} value={form.hormonal_symptoms} onChange={(v) => set("hormonal_symptoms", v)} testidPrefix="horm-sym" /></div>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Emotional &amp; cognitive symptoms</Label>
                <div className="mt-3"><ChipGroup options={EMOTIONAL_SYMPTOMS} value={form.emotional_symptoms} onChange={(v) => set("emotional_symptoms", v)} testidPrefix="emo-sym" /></div>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Nutrient deficiency indicators</Label>
                <div className="mt-3"><ChipGroup options={NUTRIENT_INDICATORS} value={form.nutrient_indicators} onChange={(v) => set("nutrient_indicators", v)} testidPrefix="nutri-ind" /></div>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Current diagnosed conditions (optional)</Label>
                <p className="text-xs text-slate-500 mt-1">Known clinical diagnoses help us tailor protocols safely — separate from symptoms above.</p>
                <div className="mt-3"><ChipGroup options={DIAGNOSED_CONDITIONS} value={form.diagnosed_conditions} onChange={(v) => set("diagnosed_conditions", v)} testidPrefix="diag-cond" /></div>
              </div>
            </Section>
          )}

          {step === 3 && (
            <Section title="Daily rhythms" subtitle="03 · Lifestyle">
              <div>
                <div className="flex justify-between"><Label className="text-sm text-slate-700">Stress level</Label><span className="text-sm font-medium text-purple-900">{form.stress_level}/10</span></div>
                <Slider data-testid="intake-stress" className="mt-3" value={[form.stress_level]} onValueChange={(v) => set("stress_level", v[0])} min={1} max={10} step={1} />
              </div>
              <div>
                <div className="flex justify-between"><Label className="text-sm text-slate-700">Sleep (hours/night)</Label><span className="text-sm font-medium text-purple-900">{form.sleep_hours}h</span></div>
                <Slider data-testid="intake-sleep" className="mt-3" value={[form.sleep_hours]} onValueChange={(v) => set("sleep_hours", v[0])} min={3} max={12} step={0.5} />
              </div>
              <div>
                <div className="flex justify-between"><Label className="text-sm text-slate-700">Water intake</Label><span className="text-sm font-medium text-purple-900">{form.water_intake_oz} oz</span></div>
                <Slider data-testid="intake-water" className="mt-3" value={[form.water_intake_oz]} onValueChange={(v) => set("water_intake_oz", v[0])} min={16} max={160} step={4} />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-sm text-slate-700">Medications</Label>
                  <Textarea data-testid="intake-medications" className="mt-2 rounded-xl border-slate-200 bg-slate-50/60" value={form.medications} onChange={(e) => set("medications", e.target.value)} placeholder="List any current medications" />
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Allergies</Label>
                  <Textarea data-testid="intake-allergies" className="mt-2 rounded-xl border-slate-200 bg-slate-50/60" value={form.allergies} onChange={(e) => set("allergies", e.target.value)} placeholder="Food, environmental, topical" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-sm text-slate-700">Exercise frequency</Label>
                  <Select value={form.exercise_frequency} onValueChange={(v) => set("exercise_frequency", v)}>
                    <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-exercise"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{EXERCISE_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Bowel movement frequency</Label>
                  <Select value={form.bowel_frequency} onValueChange={(v) => set("bowel_frequency", v)}>
                    <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-bowel"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{BOWEL_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Dietary pattern</Label>
                  <Select value={form.dietary_pattern} onValueChange={(v) => set("dietary_pattern", v)}>
                    <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-diet"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{DIETARY_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Alcohol use</Label>
                  <Select value={form.alcohol_use} onValueChange={(v) => set("alcohol_use", v)}>
                    <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-alcohol"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{ALCOHOL_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Tobacco use</Label>
                  <Select value={form.tobacco_use} onValueChange={(v) => set("tobacco_use", v)}>
                    <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-tobacco"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{TOBACCO_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Primary stressor</Label>
                  <Select value={form.primary_stressor} onValueChange={(v) => set("primary_stressor", v)}>
                    <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-stressor"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{PRIMARY_STRESSOR_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Previous functional testing (optional)</Label>
                <p className="text-xs text-slate-500 mt-1">Helps us know which results may already be available for review.</p>
                <div className="mt-3"><ChipGroup options={FUNCTIONAL_TESTING_OPTIONS} value={form.functional_testing_history} onChange={(v) => set("functional_testing_history", v)} testidPrefix="ftest" /></div>
              </div>
              <div className="rounded-2xl border border-purple-100 bg-purple-50/40 p-4 space-y-3">
                <div>
                  <Label className="text-sm text-slate-700">Do you currently have laboratory results available?</Label>
                  <p className="text-xs text-slate-500 mt-1">Helps your practitioner know what already exists to reference. Future uploads will plug in here.</p>
                  <div className="mt-3 grid grid-cols-2 gap-3 max-w-sm">
                    <button
                      type="button"
                      data-testid="intake-labs-available-yes"
                      onClick={() => set("lab_results_available", "yes")}
                      className={`h-12 rounded-xl border text-sm font-medium transition ${form.lab_results_available === "yes" ? "bg-purple-900 text-white border-purple-900" : "bg-white text-slate-700 border-slate-200 hover:border-purple-400"}`}
                    >Yes</button>
                    <button
                      type="button"
                      data-testid="intake-labs-available-no"
                      onClick={() => { set("lab_results_available", "no"); set("lab_panels_available", []); }}
                      className={`h-12 rounded-xl border text-sm font-medium transition ${form.lab_results_available === "no" ? "bg-purple-900 text-white border-purple-900" : "bg-white text-slate-700 border-slate-200 hover:border-purple-400"}`}
                    >No</button>
                  </div>
                </div>
                {form.lab_results_available === "yes" && (
                  <div>
                    <Label className="text-sm text-slate-700">Which panels do you have? (multi-select)</Label>
                    <div className="mt-3"><ChipGroup options={LAB_PANEL_OPTIONS} value={form.lab_panels_available} onChange={(v) => set("lab_panels_available", v)} testidPrefix="lab-panel" /></div>
                  </div>
                )}
              </div>
            </Section>
          )}

          {step === 4 && (
            <Section title="Your goals" subtitle="04 · Vision">
              <div>
                <Label className="text-sm text-slate-700">What does success look like in 12 weeks?</Label>
                <Textarea data-testid="intake-goals" className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 min-h-[160px]" value={form.goals} onChange={(e) => set("goals", e.target.value)} placeholder="Be specific — habits, feelings, milestones..." />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-sm text-slate-700">Primary wellness goal</Label>
                  <Select value={form.primary_wellness_goal} onValueChange={(v) => set("primary_wellness_goal", v)}>
                    <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-primary-goal"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{PRIMARY_WELLNESS_GOAL_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Preferred wellness approach</Label>
                  <Select value={form.preferred_approach} onValueChange={(v) => set("preferred_approach", v)}>
                    <SelectTrigger className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" data-testid="intake-preferred-approach"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{PREFERRED_APPROACH_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <div className="flex justify-between">
                  <Label className="text-sm text-slate-700">Readiness level</Label>
                  <span className="text-sm font-medium text-purple-900">{form.readiness_level}/10</span>
                </div>
                <Slider data-testid="intake-readiness" className="mt-3" value={[form.readiness_level]} onValueChange={(v) => set("readiness_level", v[0])} min={1} max={10} step={1} />
                <p className="text-xs text-slate-500 mt-2">How ready do you feel to commit to a 12-week protocol right now?</p>
              </div>
              <div className="rounded-xl bg-purple-50 border border-purple-100 p-5 flex gap-3">
                <CheckCircle2 className="h-5 w-5 text-purple-900 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">By submitting, you acknowledge KCIS™ provides educational wellness recommendations only and is not a substitute for licensed medical advice.</p>
              </div>
            </Section>
          )}

          <div className="mt-10 flex justify-between gap-3">
            <Button variant="outline" onClick={back} disabled={step === 1} className="rounded-full border-slate-300" data-testid="intake-back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            {step < totalSteps ? (
              <Button onClick={next} className="rounded-full bg-purple-900 hover:bg-purple-800 text-white px-8" data-testid="intake-next-btn">
                Continue <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="rounded-full bg-purple-900 hover:bg-purple-800 text-white px-8" data-testid="intake-submit-btn">
                {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>) : (<>Generate Protocol <ArrowRight className="h-4 w-4 ml-2" /></>)}
              </Button>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
